import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getActiveOutletId, setActiveOutlet } from "../../utils/helpers/outletContext";
import OutletPickerModal, { OutletIcon } from "./OutletPickerModal";
import "./outlet-switcher.css";

const ChevronIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="m9 18 6-6-6-6" />
  </svg>
);

const isAccessibleOutlet = (outlet) => ![false, 0, "0"].includes(outlet?.is_active);

const resolveActiveOutletId = (outlets, currentOutletId) => {
  const candidates = [getActiveOutletId(), Number(currentOutletId), Number(outlets[0]?.id)];
  return candidates.find((candidate) => outlets.some((outlet) => Number(outlet.id) === Number(candidate))) || null;
};

const OutletSwitcher = ({ userProfile, compact = false, menu = false, className = "" }) => {
  const outlets = useMemo(
    () => (Array.isArray(userProfile?.outlets) ? userProfile.outlets.filter(isAccessibleOutlet) : []),
    [userProfile?.outlets],
  );
  const initialId = resolveActiveOutletId(outlets, userProfile?.current_outlet_id);
  const [activeId, setActiveId] = useState(initialId);
  const [selectedId, setSelectedId] = useState(initialId);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);

  useEffect(() => {
    const resolvedId = resolveActiveOutletId(outlets, userProfile?.current_outlet_id);

    if (resolvedId && Number(resolvedId) !== Number(activeId)) {
      setActiveId(resolvedId);
    }

    if (!isPickerOpen && resolvedId && Number(resolvedId) !== Number(selectedId)) {
      setSelectedId(resolvedId);
    }
  }, [userProfile?.current_outlet_id, outlets, activeId, selectedId, isPickerOpen]);

  const activeOutlet = outlets.find((outlet) => Number(outlet.id) === Number(activeId)) || outlets[0];
  const activeOutletId = Number(activeOutlet?.id) || null;

  const openPicker = () => {
    if (outlets.length <= 1) return;
    setSelectedId(Number(activeOutlet.id));
    setIsPickerOpen(true);
  };

  const closePicker = useCallback(() => {
    if (isSwitching) return;
    setSelectedId(activeOutletId);
    setIsPickerOpen(false);
  }, [activeOutletId, isSwitching]);

  const applyOutlet = () => {
    const nextOutlet = outlets.find((outlet) => Number(outlet.id) === Number(selectedId));
    if (!nextOutlet || Number(nextOutlet.id) === Number(activeOutlet.id) || isSwitching) return;

    setIsSwitching(true);
    setActiveId(Number(nextOutlet.id));
    setActiveOutlet(nextOutlet);
    window.location.reload();
  };

  if (!activeOutlet) return null;

  const switcherClassName = `outlet-switcher ${compact ? "is-compact" : ""} ${menu ? "is-menu" : ""} ${
    outlets.length > 1 ? "is-interactive" : "is-static"
  } ${className}`.trim();

  const content = (
    <>
      <span className="outlet-switcher-icon"><OutletIcon /></span>
      <span className="outlet-switcher-copy">
        <small>Outlet aktif</small>
        <strong>{activeOutlet.name}</strong>
      </span>
      {outlets.length > 1 ? (
        <span className="outlet-switcher-chevron" aria-hidden="true"><ChevronIcon /></span>
      ) : (
        <span className="outlet-live-dot" aria-label="Outlet aktif" />
      )}
    </>
  );

  return (
    <>
      {outlets.length > 1 ? (
        <button
          type="button"
          className={switcherClassName}
          title={`Ganti outlet. Saat ini: ${activeOutlet.name}`}
          aria-label={`Pilih outlet aktif. Saat ini ${activeOutlet.name}`}
          aria-haspopup="dialog"
          aria-expanded={isPickerOpen}
          onClick={openPicker}>
          {content}
        </button>
      ) : (
        <div className={switcherClassName} title={`Outlet aktif: ${activeOutlet.name}`}>
          {content}
        </div>
      )}

      <OutletPickerModal
        open={isPickerOpen}
        outlets={outlets}
        activeOutlet={activeOutlet}
        selectedId={selectedId}
        switching={isSwitching}
        onSelect={(outlet) => setSelectedId(Number(outlet.id))}
        onClose={closePicker}
        onConfirm={applyOutlet}
      />
    </>
  );
};

export default OutletSwitcher;
